import React, { useEffect, useRef, useState } from "react";
import classnames from "classnames";
import { v3 } from "oc-threejs/short3";
import * as Textures from "oc-threejs/Textures";
import * as Maths from "oc-commons/Maths";
import * as Scenes from "oc-threejs/Scenes";
import { OcAxisHelper } from "oc-threejs/objects/OcAxisHelper";
import "./SceneExplorer.scss";
import * as Commons from "oc-commons/Commons";
import * as Screens from "oc-threejs/Screens";
import {
  BoxGeometry,
  BufferGeometry,
  Color,
  EdgesGeometry,
  Euler,
  Group,
  Line,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  Object3D,
} from "three";

const debugAxisParentMat = new LineBasicMaterial({
  color: new Color("hsl(0, 0%, 0%)"),
});
const debugAxisSelfMat = new LineBasicMaterial({
  color: new Color("hsl(0, 0%, 47%)"),
});
const debugMat1 = new LineBasicMaterial({
  linewidth: 2,
  color: new Color("hsl(0, 0%, 47%)"),
});

const getMeta = (object3d) => {
  if (object3d.getMeta) {
    return object3d.getMeta();
  }
  if (object3d.meta) {
    return object3d.meta;
  }
  return null;
};
const getName = (object3d) => {
  let key = object3d.key ? object3d.key : getMeta(object3d)?.Key ? getMeta(object3d)?.Key : null;
  return (
    Commons.typeName(object3d) + (object3d.id ? ` (${object3d.id})` : "") + (key ? " : " + key : "")
  );
};
const decimals = 4;
const vectorToString = (v) =>
  "x:" +
  Maths.round(v.x, decimals) +
  " y:" +
  Maths.round(v.y, decimals) +
  " z:" +
  Maths.round(v.z, decimals);
const rotationToString = (quaternion) => {
  const rotation = (
    new Euler().setFromQuaternion(quaternion, "XYZ").toArray().slice(0, 3) as number[]
  ).map((x) => Maths.round((x * 180) / Math.PI, decimals) + "°");
  return "x:" + rotation[0] + " y:" + rotation[1] + " z:" + rotation[2];
};
const printBox = (box) => `  Min = ${vectorToString(box.min)}
  Max = ${vectorToString(box.max)}
  Size = ${vectorToString(box.getSize(v3()))}`;
const getObject3dLocalBox = (object3d) => {
  if (object3d.jaco) {
    return object3d.jaco.getBox();
  }
  return Scenes.boundingBoxLocalMatrix(
    object3d,
    (o) =>
      o.visible !== false &&
      (o as any).material?.visible !== false &&
      (o.opacity == null || o.opacity > 0.01),
  );
};
const getObject3dWorldBox = (object3d) => {
  if (object3d.jaco) {
    return object3d.jaco.getBox().applyMatrix4(object3d.jaco.matrixWorld);
  }
  return Scenes.boundingBox(
    object3d,
    (o) =>
      o.visible !== false &&
      (o as any).material?.visible !== false &&
      (o.opacity == null || o.opacity > 0.01),
  );
};
export const getDetailsText = (object3d) => {
  const bl = printBox(getObject3dLocalBox(object3d));
  const bw = printBox(getObject3dWorldBox(object3d));
  return `${getName(object3d)}
coordinate system relative to parent:
  Position = ${vectorToString(object3d.position)}
  Rotation = ${rotationToString(object3d.quaternion)}
${
  bl != bw
    ? `Bounding Box Local:
${bl}
`
    : ""
}Bounding Box World :
${bw}
`;
};

/**
 * Eine React-Komponente zum Debuggen von Object3d-3D-Szenen
 * Angezeigt wird ein aufklappbarer Baum der Object3d-Szene mit Anzeige der Koordinatensysteme.
 */
export function SceneExplorer({ axisLabeled, scene, sceneNode, setSelected, isTopNode }) {
  const containerRef = useRef(null);
  const [selectedObject3d, _setSelectedObject3d] = useState(null);
  const [search, setSearch] = useState("");
  const [detailsText, setDetailsText] = useState("");
  const [openSet, setOpenSet] = useState(new Set());
  const [, forceUpdate] = useState(null);

  useEffect(() => {
    const domElement = APP.renderer.domElement;
    const listener = (e) => {
      if (!isTopNode) {
        return;
      }
      const clickedMesh = Screens.fetchObjectAtMouse(
        e,
        Scenes.filter(scene, (o) => o instanceof Mesh),
      );
      if (!clickedMesh) {
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();

      let o = clickedMesh;
      while (o != null) {
        openSet.add(o);
        o = o.parent;
      }
      setOpenSet(new Set(openSet));

      const selected = clickedMesh;
      setSelectedObject3d(selected);
      const objectDiv = containerRef.current?.querySelector(
        '.object3d[data-uuid="' + selected.uuid + '"]',
      );
      if (objectDiv) {
        objectDiv.scrollIntoView();
      }
    };
    domElement.addEventListener("click", listener, { capture: true });
    if (APP.interactionHandler) {
      APP.interactionHandler.locked = true;
    }
    return () => {
      setSelectedObject3d(null);
      domElement.removeEventListener("click", listener);
      if (APP.interactionHandler) {
        APP.interactionHandler.locked = false;
      }
    };
  }, [scene, isTopNode]);

  const renderObject3d = (object3d) => {
    if (object3d == null) {
      return "null";
    }
    const children = getChildren(object3d);
    const hasChildren = children.length > 0;
    const open = children.length > 0 && isOpen(object3d);
    return (
      <div className="object3d" key={object3d.id} data-uuid={object3d.uuid}>
        <span className="nameRow">
          {hasChildren ? (
            <span className="tree-point hoverunderscore" onClick={() => toggleOpen(object3d)}>
              {open ? "▼" : "▲"}
            </span>
          ) : (
            <span className="tree-point">•</span>
          )}
          <span className="tree-point hoverunderscore" onClick={() => toggleVisible(object3d)}>
            {object3d.visible ? "👁" : "⎯"}
          </span>
          <span
            className={classnames(
              "hoverunderscore",
              "object-name",
              selectedObject3d === object3d ? "selected" : null,
            )}
            title={getName(object3d)}
            onClick={() => setSelectedObject3d(object3d)}
          >
            {getName(object3d)}
          </span>
        </span>
        {open && <div className="children">{children.map((child) => renderObject3d(child))}</div>}
      </div>
    );
  };

  const getParentAndSelfSet = (object3d) => {
    const parents = new Set();
    let p = object3d;
    while (p != null) {
      parents.add(p);
      p = p.parent;
    }
    return parents;
  };

  const getChildren = (object3d) => {
    const visited = getParentAndSelfSet(object3d);
    const children = [];
    const maxDepth = 30;
    const iterateTree = (value, depth) => {
      if (depth > maxDepth) {
        return;
      }
      if (value == null || _.isNumber(value) || _.isString(value) || _.isBoolean(value)) {
        return;
      }
      if (visited.has(value)) {
        return;
      }
      visited.add(value);
      if (Array.isArray(value) || value instanceof Set || value instanceof Map) {
        value.forEach((v) => iterateTree(v, depth + 1));
      } else if (_.isPlainObject(value)) {
        Object.values(value).forEach((v) => iterateTree(v, depth + 1));
      } else if (value instanceof Object3D) {
        children.push(value);
      }
    };
    Object.values(object3d).forEach(iterateTree, 0);
    return children;
  };

  const isOpen = (object3d) => {
    return openSet.has(object3d);
  };

  const toggleOpen = (object3d) => {
    if (isOpen(object3d)) {
      openSet.delete(object3d);
    } else {
      openSet.add(object3d);
    }
    setOpenSet(new Set(openSet));
  };

  const setSelectedObject3d = (object3d) => {
    if (object3d == null) {
      if (scene.debugContainer != null) {
        Scenes.remove(scene.debugContainer);
        scene.debugContainer = null;
        setDetailsText("");
      }
      return;
    }

    console.log(object3d);

    _setSelectedObject3d(object3d);

    const initDebugContainer = () => {
      if (scene.debugContainer == null) {
        scene.debugContainer = new Group();
        scene.debugContainer.name = "scene-explorer-debug-container";
        STAGE.setSecondRenderPass();
        STAGE.secondPassScene.add(scene.debugContainer);
      }
      Scenes.empty(scene.debugContainer);
    };

    const drawAxis = (matrixWorld, length, material) => {
      const axis = new Group();
      axis.name = "scene-explorer-axis";
      axis.applyMatrix4(matrixWorld);
      if (axisLabeled) {
        axis.add(new OcAxisHelper(length));
      } else {
        const line = (v) => {
          const line = new Line(
            new BufferGeometry().setFromPoints([v, v.clone().negate()]),
            material,
          );
          line.computeLineDistances();
          axis.add(line);
          return line;
        };
        line(v3(length, 0, 0));
        line(v3(0, length, 0));
        line(v3(0, 0, length));
      }
      scene.debugContainer.add(axis);
    };

    const drawBox = (box, material) => {
      const size = box.getSize(v3());
      const edges = new EdgesGeometry(new BoxGeometry(size.x, size.y, size.z));
      const line = new LineSegments(edges, material);
      line.name = "scene-explorer-box";
      box.getCenter(line.position);
      scene.debugContainer.add(line);
    };

    const drawGeometry = (geometry, material) => {
      const edges = new EdgesGeometry(geometry);
      const line = new LineSegments(edges, material);
      line.name = "scene-explorer-geometry";
      line.computeLineDistances();
      scene.debugContainer.add(line);
      return line;
    };

    const drawText = (text, position, color) => {
      text.split("\n").forEach((line, lineIndex) => {
        const height = 0.1;
        const sprite = Textures.createTextSprite(line, {
          cameraIndependendSize: false,
          orginalHeight: height,
          pixelPerUnit: 500,
          color,
          backgroundColor: "transparent",
        });
        sprite.position.copy(position.clone().add(v3(0, -height * lineIndex, 0)));
        scene.debugContainer.add(sprite);
      });
    };

    object3d.updateMatrixWorld();

    initDebugContainer();

    object3d.parent && drawAxis(object3d.parent.matrixWorld, 1000, debugAxisParentMat);
    drawAxis(object3d.matrixWorld, 1, debugAxisSelfMat);

    drawBox(getObject3dWorldBox(object3d), debugMat1);

    setDetailsText(getDetailsText(object3d));

    dirty();
  };

  const toggleVisible = (object3d) => {
    object3d.visible = !object3d.visible;
    dirty();
  };

  const dirty = () => {
    STAGE.dirty();
    forceUpdate({});
  };

  let objects = [scene];
  if (search != "") {
    const lowerSearch = search.toLowerCase();
    objects = Scenes.filter(
      scene,
      (o) =>
        o.name?.toString().toLowerCase()?.contains(lowerSearch) ||
        o.constructor.name.toString().toLowerCase().contains(lowerSearch),
    );
  }

  let modelNodes = [];
  if (selectedObject3d) {
    if (selectedObject3d.__sceneNodeParentEntry) {
      modelNodes.push(selectedObject3d.__sceneNodeParentEntry);
    }

    const metaToMetatable = (meta) => APP.richMetadata.rowToMetatableMap.get(meta);

    const metaToInfo = (meta, tables) => {
      const metatable = metaToMetatable(meta);
      if (!tables.includes(metatable)) {
        return null;
      }
      return [APP.sceneNode.metaToNodeMap.get(meta), sceneNode.findProperty(metatable.name)];
    };

    const mmrTabel = APP.richMetadata.metaTableMap.ModelMaterialRelation;
    const modelTabel = APP.richMetadata.metaTableMap.Model;

    const metas = new Set(Scenes.map(selectedObject3d, (o: any) => o.getMeta && o.getMeta()))
      .filter((meta) => meta != null)
      .toArray() as any;

    const furtherModelNodes = metas
      .map(
        (meta) =>
          metaToInfo(meta, [mmrTabel, modelTabel]) ??
          metaToInfo(meta["ModelMaterialRelation"]?.[0], [mmrTabel]) ??
          metaToInfo(meta["Model"]?.[0], [modelTabel]) ??
          [],
      )
      .filter(([node, parentNodeList]) => node && parentNodeList)
      .map(([node, parentNodeList]) => ({
        name: node.getName(),
        node,
        parentNodeList,
      }));

    modelNodes = modelNodes.concat(furtherModelNodes);
  }

  const materialNodeList = sceneNode.findProperty("MaterialJson");

  const materialNamesFromObject = () => Textures.getMaterialNames(selectedObject3d);

  const materialNamesFromGhosts = () =>
    new Set(
      Scenes.filter(selectedObject3d, (o) => (o as any).isModelGhost3d).map(
        (o) => (o as any).model3d,
      ),
    )
      .toArray()
      .flatMap((o) => Textures.getMaterialNames(o));

  const materialNames =
    selectedObject3d &&
    new Set(materialNamesFromObject().concat(materialNamesFromGhosts()))
      .toArray()
      .filter((name) => materialNodeList.findKey(name));

  return (
    <div className="SceneExplorer flex-column" ref={containerRef}>
      <input
        type="text"
        placeholder="search"
        className="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="tree">{objects.map((o) => renderObject3d(o))}</div>
      {selectedObject3d && modelNodes.length > 0 && (
        <div className="lists">
          Modelle:
          <ul>
            {modelNodes.map((model) => (
              <li
                key={model.name}
                className="button"
                onClick={() => {
                  setSelected(model.node, model.parentNodeList);
                }}
              >
                {model.name}
              </li>
            ))}
          </ul>
        </div>
      )}
      {selectedObject3d && materialNames.length > 0 && (
        <div className="lists">
          Materialien:
          <ul>
            {materialNames.map((name) => (
              <li
                key={name + ""}
                className="button"
                onClick={() => {
                  setSelected(materialNodeList.findKey(name), materialNodeList);
                }}
              >
                {name}
              </li>
            ))}
          </ul>
        </div>
      )}
      {selectedObject3d && (
        <div className="lists">
          3D-Infos:
          <div className="details">{detailsText}</div>
        </div>
      )}
    </div>
  );
}
